import {
  PAYMENT_REVIEW_REASONS,
  paymentPurchaseActionSchema,
} from "@ai-crm/shared";
import type {
  AdminPaymentDTO,
  PaymentCorrectionInput,
  PaymentReviewPreviewDTO,
  PaymentReviewPreviewInput,
  PaymentReviewReason,
  BillingPayerDTO,
  PaymentPurchaseAction,
} from "@ai-crm/shared";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { Pagination } from "../../components/pagination";
import { adminApi, ApiError } from "../../lib/api";
import { formatMoscowDateTime, formatPaymentRub } from "../../lib/format";
import {
  paymentButton,
  paymentPrimary,
  paymentError,
  paymentKeys,
  paymentReviewLabels,
  usePaymentScope,
  usePaymentWrite,
  usePreviewExpiry,
} from "../../lib/payment-ui";
import type { InvoiceActionProps } from "./payment-application-panel";
import {
  initialPaymentRecord,
  parsePaymentRecord,
  PaymentRecordForm,
  PaymentAllocationFields,
  readAllocations,
} from "./payment-record-form";

export function PaymentCorrectionForm({
  accountId,
  payment,
  onChanged,
}: {
  accountId: string;
  payment: AdminPaymentDTO;
  onChanged: () => Promise<void>;
}) {
  const scope = usePaymentScope(accountId),
    write = usePaymentWrite(accountId);
  const [reason, setReason] = useState("");
  const [replace, setReplace] = useState(false);
  const [draft, setDraft] = useState(() =>
    initialPaymentRecord(payment.payer, payment.amountRub, payment),
  );
  const [dialog, setDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  async function confirm() {
    if (!scope.token || write.locked) return;
    let replacement;
    try {
      if (!reason.trim()) throw new Error("Укажите причину исправления.");
      replacement = replace ? parsePaymentRecord(draft) : null;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Проверьте данные");
      setDialog(false);
      return;
    }
    const token = scope.token;
    const input: PaymentCorrectionInput = {
      requestId: crypto.randomUUID(),
      expectedVersion: payment.version,
      reason: reason.trim(),
      replacement,
    };
    setDialog(false);
    setError(null);
    await write.run(
      input,
      (body) => adminApi.correctPayment(token, accountId, payment.id, body),
      async () => {
        setMessage("Исправление сохранено. История исходной записи сохранена.");
        await onChanged();
      },
      async (caught) => {
        if (caught instanceof ApiError && caught.status === 409)
          await onChanged();
      },
    );
  }
  return (
    <details className="rounded-md border p-3">
      <summary className="cursor-pointer text-sm font-medium">
        Исправить запись
      </summary>
      <div className="mt-3 space-y-3">
        <label className="block space-y-1 text-sm">
          <span>Причина исправления</span>
          <textarea
            className="control"
            value={reason}
            disabled={write.locked}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <label className="flex gap-2 text-sm">
          <input
            type="checkbox"
            checked={replace}
            disabled={write.locked}
            onChange={(e) => setReplace(e.target.checked)}
          />
          Создать исправленную запись
        </label>
        {replace && (
          <PaymentRecordForm
            value={draft}
            disabled={write.locked}
            onChange={setDraft}
          />
        )}
        {!write.uncertain && (
          <button
            className={paymentButton}
            disabled={
              write.locked || !reason.trim() || payment.recordState === "ERROR"
            }
            onClick={() => setDialog(true)}
          >
            Подтвердить исправление
          </button>
        )}
        {write.uncertain && (
          <button
            className={paymentButton}
            disabled={write.pending}
            onClick={() => void write.retry()}
          >
            Повторить исправление
          </button>
        )}
        {dialog && (
          <ConfirmDialog
            title="Исправить банковскую запись"
            description="Исходная запись будет помечена ошибочной. Денежные факты и история не удаляются; уже предоставленный доступ автоматически не отменяется."
            confirmLabel="Исправить запись"
            isPending={write.pending}
            onCancel={() => setDialog(false)}
            onConfirm={() => void confirm()}
          />
        )}
        {(error || write.error) && (
          <p role="alert" className="text-sm text-danger">
            {error ?? write.error}
          </p>
        )}
        {message && <p role="status">{message}</p>}
      </div>
    </details>
  );
}

export function PaymentReviewPanel({
  accountId,
  invoice,
  onChanged,
}: InvoiceActionProps) {
  const scope = usePaymentScope(accountId),
    write = usePaymentWrite(accountId);
  const [decision, setDecision] =
    useState<PaymentReviewPreviewInput["decision"]>("ACCEPT_ORIGINAL");
  const [reason, setReason] = useState<PaymentReviewReason>("MANUAL_REVIEW");
  const [note, setNote] = useState("");
  const [agreement, setAgreement] = useState("");
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [paymentIds, setPaymentIds] = useState<string[]>([]);
  const [kind, setKind] = useState<PaymentPurchaseAction["kind"]>(
    invoice.billing.allowedActions.assign
      ? "ASSIGN"
      : invoice.billing.allowedActions.renew
        ? "RENEW"
        : invoice.billing.allowedActions.topup
          ? "TOPUP"
          : "UPGRADE",
  );
  const [tariffId, setTariffId] = useState("");
  const [volume, setVolume] = useState("1"),
    [months, setMonths] = useState("1");
  const [payer, setPayer] = useState<BillingPayerDTO | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [preview, setPreview] = useState<PaymentReviewPreviewDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const previewPending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [dialog, setDialog] = useState(false);
  const expired = usePreviewExpiry(preview?.expiresAt);
  const enabled = Boolean(
    scope.token &&
    invoice.allowedCommands.previewReview &&
    decision === "REPLACE_INVOICE",
  );
  const catalog = useQuery({
    queryKey: [
      ...paymentKeys.account(scope.adminId, accountId),
      "replacement-catalog",
    ],
    queryFn: ({ signal }) =>
      scope.read(signal, () => adminApi.tariffs(scope.token!)),
    enabled,
  });
  const recipient = useQuery({
    queryKey: paymentKeys.recipient(scope.adminId),
    queryFn: ({ signal }) =>
      scope.read(signal, () => adminApi.paymentRecipient(scope.token!, signal)),
    enabled,
  });
  const cursor = cursorStack.at(-1);
  const payers = useQuery({
    queryKey: [
      ...paymentKeys.account(scope.adminId, accountId),
      "payers",
      cursor ?? "",
    ],
    queryFn: ({ signal }) =>
      scope.read(signal, () =>
        adminApi.paymentPayers(
          scope.token!,
          accountId,
          { cursor, limit: 10, archived: "active" },
          signal,
        ),
      ),
    enabled,
  });
  const locked = write.locked || loading;
  const kinds = (["ASSIGN", "RENEW", "TOPUP", "UPGRADE"] as const).filter(
    (v) =>
      invoice.billing.allowedActions[
        v.toLowerCase() as keyof typeof invoice.billing.allowedActions
      ],
  );
  const selectedTariff = catalog.data?.definition.paidTariffs.find(
    (t) => t.id === tariffId,
  );
  const minimum =
    kind === "UPGRADE"
      ? invoice.billing.upgradeMinimums.find((m) => m.tariffId === tariffId)
          ?.monthlyDialogs
      : selectedTariff?.monthlyDialogs;
  function changed() {
    setPreview(null);
    setError(null);
    setMessage(null);
    write.clearError();
  }
  async function refreshConflict(caught: unknown) {
    if (caught instanceof ApiError && caught.status === 409) {
      setPreview(null);
      setDialog(false);
      await onChanged();
    }
  }
  async function requestPreview() {
    if (!scope.token || locked || previewPending.current) return;
    const guard = scope.capture();
    previewPending.current = true;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      if (!note.trim()) throw new Error("Укажите результат сверки.");
      if (
        (decision === "REPLACE_INVOICE" ||
          reason === "SOURCE_CHANGED" ||
          reason === "PERIOD_EXPIRED") &&
        !agreement.trim()
      )
        throw new Error("Зафиксируйте согласование условий с клиентом.");
      let replacement: PaymentReviewPreviewInput["replacement"] = null;
      if (decision === "REPLACE_INVOICE") {
        const issuer = recipient.data?.activeVersion;
        if (!catalog.data || !issuer || !payer || payer.archivedAt)
          throw new Error(
            "Загрузите каталог, активные реквизиты получателя и выберите компанию.",
          );
        if (!kinds.includes(kind))
          throw new Error("Выберите разрешённую покупку.");
        if (
          kind !== "TOPUP" &&
          (!selectedTariff || minimum == null || Number(volume) < minimum)
        )
          throw new Error("Выберите тариф и допустимый месячный объём.");
        if (
          (kind === "ASSIGN" || kind === "RENEW") &&
          !selectedTariff?.terms.some((t) => t.months === Number(months))
        )
          throw new Error("Выберите доступный срок.");
        const action = paymentPurchaseActionSchema.parse(
          kind === "TOPUP"
            ? { kind, dialogs: Number(volume) }
            : kind === "UPGRADE"
              ? { kind, tariffId, monthlyDialogs: Number(volume) }
              : {
                  kind,
                  tariffId,
                  monthlyDialogs: Number(volume),
                  months: Number(months),
                },
        );
        replacement = {
          action,
          expectedCatalogVersion: invoice.billing.billing.catalogVersion,
          expectedBillingVersion: invoice.billing.billing.billingVersion,
          payerId: payer.id,
          expectedPayerVersion: payer.version,
          expectedRecipientVersion: issuer.id,
        };
      }
      const result = await adminApi.previewPaymentReview(
        scope.token,
        accountId,
        invoice.id,
        {
          expectedVersion: invoice.version,
          decision,
          reasonCode: reason,
          note: note.trim(),
          customerAgreement: agreement.trim() || null,
          paymentIds,
          allocations:
            decision === "REPLACE_INVOICE"
              ? []
              : readAllocations(
                  Object.fromEntries(
                    Object.entries(allocations).filter(([id]) =>
                      paymentIds.includes(id),
                    ),
                  ),
                ),
          replacement,
        },
      );
      if (guard.isCurrent()) setPreview(result);
    } catch (caught) {
      if (guard.isCurrent()) {
        setError(
          caught instanceof ApiError
            ? paymentError(caught)
            : caught instanceof Error
              ? caught.message
              : "Проверьте данные",
        );
        await refreshConflict(caught);
      }
    } finally {
      if (guard.isCurrent()) {
        previewPending.current = false;
        setLoading(false);
      }
    }
  }
  async function confirm() {
    if (!scope.token || !preview || locked || expired) return;
    const token = scope.token;
    const input = {
      requestId: crypto.randomUUID(),
      expectedVersion: preview.invoiceVersion,
      previewToken: preview.previewToken,
    };
    let nextId: string | undefined;
    setDialog(false);
    await write.run(
      input,
      async (body) => {
        const result = await adminApi.confirmPaymentReview(
          token,
          accountId,
          invoice.id,
          body,
        );
        nextId = result.replacementInvoice?.id;
      },
      async () => {
        setPreview(null);
        setMessage(
          "Решение сверки сохранено. Применение покупки выполняется отдельно.",
        );
        await onChanged(nextId);
      },
      refreshConflict,
    );
  }
  return (
    <section
      className="space-y-4 rounded-md border p-4"
      aria-label="Ручная сверка"
    >
      <h3 className="font-semibold">Ручная сверка</h3>
      {invoice.reviews.map((r) => (
        <div key={r.id} className="rounded-md border p-3 text-sm">
          <p>
            {paymentReviewLabels[r.reasonCode]} ·{" "}
            {formatMoscowDateTime(r.createdAt)} МСК
          </p>
          <p className="whitespace-pre-wrap break-words">{r.note}</p>
          {r.customerAgreement && (
            <p className="break-words">Согласовано: {r.customerAgreement}</p>
          )}
          <p>
            {r.provisionAllowed
              ? "Предоставление разрешено сервером"
              : "Требуется проверка"}
          </p>
        </div>
      ))}
      {invoice.allowedCommands.previewReview && (
        <>
          <fieldset disabled={locked} className="space-y-3">
            <legend className="font-medium">Решение</legend>
            <select
              className="control"
              aria-label="Решение сверки"
              value={decision}
              onChange={(e) => {
                setDecision(e.target.value as typeof decision);
                changed();
              }}
            >
              <option value="ACCEPT_ORIGINAL">
                Принять по исходному счёту
              </option>
              <option
                value="REPLACE_INVOICE"
                disabled={Boolean(invoice.application)}
              >
                Новый счёт и перенос поступлений
              </option>
            </select>
            <label className="block space-y-1 text-sm">
              <span>Причина сверки</span>
              <select
                className="control"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value as PaymentReviewReason);
                  changed();
                }}
              >
                {PAYMENT_REVIEW_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {paymentReviewLabels[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1 text-sm">
              <span>Результат сверки</span>
              <textarea
                className="control"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  changed();
                }}
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span>Согласование с клиентом</span>
              <textarea
                className="control"
                value={agreement}
                onChange={(e) => {
                  setAgreement(e.target.value);
                  changed();
                }}
              />
            </label>
            {invoice.payments
              .filter(
                (p) =>
                  p.recordState === "VALID" &&
                  (decision !== "REPLACE_INVOICE" || p.allocatedRub === "0"),
              )
              .map((p) => (
                <label key={p.id} className="flex gap-2 break-all text-sm">
                  <input
                    type="checkbox"
                    checked={paymentIds.includes(p.id)}
                    onChange={(e) => {
                      setPaymentIds((ids) =>
                        e.target.checked
                          ? [...ids, p.id]
                          : ids.filter((id) => id !== p.id),
                      );
                      changed();
                    }}
                  />
                  {p.id} · {formatPaymentRub(p.amountRub)}
                </label>
              ))}
            {decision === "ACCEPT_ORIGINAL" ? (
              <PaymentAllocationFields
                payments={invoice.payments.filter((p) =>
                  paymentIds.includes(p.id),
                )}
                values={allocations}
                disabled={locked}
                onChange={(next) => {
                  setAllocations(next);
                  changed();
                }}
              />
            ) : (
              <>
                {(!catalog.data || !recipient.data?.activeVersion) && (
                  <p role="status">
                    Для нового счёта нужны каталог и заполненные реквизиты
                    получателя.
                  </p>
                )}
                {(catalog.isError || recipient.isError || payers.isError) && (
                  <p role="alert">
                    Не удалось загрузить данные.{" "}
                    <button
                      className={paymentButton}
                      type="button"
                      onClick={() => {
                        void catalog.refetch();
                        void recipient.refetch();
                        void payers.refetch();
                      }}
                    >
                      Повторить загрузку
                    </button>
                  </p>
                )}
                <label className="block space-y-1 text-sm">
                  <span>Новая покупка</span>
                  <select
                    className="control"
                    value={kind}
                    onChange={(e) => {
                      setKind(e.target.value as typeof kind);
                      changed();
                    }}
                  >
                    <option value="" disabled>
                      Выберите действие
                    </option>
                    {kinds.map((k) => (
                      <option key={k} value={k}>
                        {k === "ASSIGN"
                          ? "Подписка"
                          : k === "RENEW"
                            ? "Продление"
                            : k === "UPGRADE"
                              ? "Повышение"
                              : "Докупка"}
                      </option>
                    ))}
                  </select>
                </label>
                {kind !== "TOPUP" && (
                  <label className="block space-y-1 text-sm">
                    <span>Новый тариф</span>
                    <select
                      className="control"
                      value={tariffId}
                      onChange={(e) => {
                        const t = catalog.data?.definition.paidTariffs.find(
                          (item) => item.id === e.target.value,
                        );
                        setTariffId(e.target.value);
                        if (t) {
                          setVolume(String(t.monthlyDialogs));
                          setMonths(String(t.terms[0]?.months ?? 1));
                        }
                        changed();
                      }}
                    >
                      <option value="">Выберите тариф</option>
                      {catalog.data?.definition.paidTariffs.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="block space-y-1 text-sm">
                  <span>
                    {kind === "TOPUP"
                      ? "Диалогов докупки"
                      : "Месячный объём нового счёта"}
                  </span>
                  <input
                    className="control"
                    type="number"
                    min={minimum ?? 1}
                    value={volume}
                    onChange={(e) => {
                      setVolume(e.target.value);
                      changed();
                    }}
                  />
                </label>
                {(kind === "ASSIGN" || kind === "RENEW") && (
                  <label className="block space-y-1 text-sm">
                    <span>Срок нового счёта, месяцев по 30 дней</span>
                    <select
                      className="control"
                      value={months}
                      onChange={(e) => {
                        setMonths(e.target.value);
                        changed();
                      }}
                    >
                      {selectedTariff?.terms.map((t) => (
                        <option key={t.months} value={t.months}>
                          {t.months}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <p className="text-sm">
                  Плательщик: {payer?.name ?? "не выбран"}
                </p>
                {payers.data?.items.map((p) => (
                  <label key={p.id} className="flex gap-2 text-sm">
                    <input
                      type="radio"
                      name={`replacement-payer-${invoice.id}`}
                      checked={payer?.id === p.id}
                      onChange={() => {
                        setPayer(p);
                        changed();
                      }}
                    />
                    {p.name} · {p.inn}
                  </label>
                ))}
                <Pagination
                  canGoBack={cursorStack.length > 0}
                  canGoForward={Boolean(
                    payers.data?.nextCursor &&
                    !cursorStack.includes(payers.data.nextCursor),
                  )}
                  isLoading={payers.isFetching}
                  onBack={() => setCursorStack((s) => s.slice(0, -1))}
                  onForward={() => {
                    const next = payers.data?.nextCursor;
                    if (next && !cursorStack.includes(next))
                      setCursorStack((s) => [...s, next]);
                  }}
                />
              </>
            )}
          </fieldset>
          <button
            className={paymentButton}
            disabled={locked}
            onClick={() => void requestPreview()}
          >
            Предпросмотр решения
          </button>
        </>
      )}
      {preview && (
        <div className="space-y-2 rounded-md border p-3 text-sm">
          <p>
            {preview.provisionAllowed
              ? "Предоставление разрешено"
              : "Предоставление не разрешено"}
            ; незачтённый остаток: {formatPaymentRub(preview.unallocatedRub)}.
          </p>
          <p>
            Остаток остаётся на ручной сверке; баланс и возврат автоматически не
            создаются.
          </p>
          {preview.replacementQuote && (
            <p>
              Новый счёт: {formatPaymentRub(preview.replacementQuote.totalRub)},{" "}
              {preview.replacementQuote.action.kind}; срок оплаты до{" "}
              {formatMoscowDateTime(
                preview.replacementQuote.estimatedInvoiceExpiresAt,
              )}
              .
            </p>
          )}
          {expired && <p>Предпросмотр истёк. Получите новый.</p>}
          {!write.uncertain && (
            <button
              className={paymentPrimary}
              disabled={
                locked ||
                expired ||
                (preview.replacementQuote !== null &&
                  preview.replacementQuote.invoiceAvailability !== "READY")
              }
              onClick={() => setDialog(true)}
            >
              Сохранить решение сверки
            </button>
          )}
        </div>
      )}
      {dialog && (
        <ConfirmDialog
          title="Сохранить решение сверки"
          description="Сохранится решение и связи поступлений. Применение покупки выполняется отдельной командой."
          confirmLabel="Сохранить решение"
          isPending={write.pending}
          onCancel={() => setDialog(false)}
          onConfirm={() => void confirm()}
        />
      )}
      {write.uncertain && (
        <button
          className={paymentButton}
          disabled={write.pending}
          onClick={() => void write.retry()}
        >
          Повторить решение сверки
        </button>
      )}
      {(error || write.error) && (
        <p role="alert" className="text-sm text-danger">
          {error ?? write.error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
